<template>
  <component
    :is="externalLink ? 'a' : 'div'"
    :href="externalLink"
    :target="externalLink ? '_blank' : undefined"
    class="feature-card"
    :class="{
      'selected': isSelected,
      'disabled': isDisabled,
      'is-active': isActive
    }"
    :style="{ '--card-color': color }"
    @click="handleClick"
  >
    <img v-if="icon" :src="icon" alt="" class="card-icon" />
    <h3 class="card-title" v-html="label.replace(/\n/g, '<br>')"></h3>
  </component>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'FeatureCard',
  props: {
    icon: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    color: {
      type: String,
      required: true
    },
    image: {
      type: String,
      default: ''
    },
    isActive: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    externalLink: {
      type: String,
      default: ''
    }
  },
  emits: ['select'],
  setup(props, { emit }) {
    const handleClick = (e: MouseEvent) => {
      if (props.isDisabled) {
        e.preventDefault()
        return
      }
      
      if (!props.externalLink) {
        emit('select')
      }
    }

    return {
      handleClick
    }
  }
})
</script>

<style scoped>
.feature-card {
  position: relative;
  background: var(--card-color, #7ee9b5);
  border: 2px solid var(--vt-c-accent);
  border-radius: var(--base-layout-card-border-radius);
  padding: clamp(0.75rem, 2dvh, 1.5rem) clamp(0.75rem, 2vw, 1.25rem);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: clamp(0.25rem, 1dvh, 0.6rem);
  width: 100%;
  box-shadow: var(--shadow-panel);
  text-decoration: none;
  box-sizing: border-box;
}

.feature-card:hover:not(.disabled) {
  transform: translateY(-4px);
  box-shadow:
    0 10px 26px rgba(80, 229, 255, 0.42),
    0 0 28px rgba(80, 229, 255, 0.38);
  border-color: #50e5ff;
}

.feature-card.selected {
  border-color: var(--vt-c-accent);
  border-width: 2px;
  background: #50e5ff;
  box-shadow: 0 12px 30px rgba(80, 229, 255, 0.32);
  transform: translateY(-4px);
}

.feature-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
  border-color: var(--vt-c-border);
}

.card-icon {
  width: clamp(2rem, 4.5dvh, 3rem);
  height: clamp(2rem, 4.5dvh, 3rem);
  object-fit: contain;
}

.card-title {
  font-size: clamp(0.9rem, 0.78rem + 0.65vw, 1.2rem);
  font-weight: 700;
  color: #212543;
  margin: 0;
  line-height: 1.2;
}

@media (max-width: 768px) {
  .feature-card {
    padding: 0.6rem 0.65rem;
    gap: 0.2rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .feature-card {
    transition: none;
  }

  .feature-card:hover:not(.disabled) {
    transform: none;
  }
}
</style>
